import { FileDoneOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { useRequest } from '../../api/hooks';
import AsyncWrapper from '@/components/AsyncWrapper';
import { CommandEntityReady, CommandEntityTypeEnum } from '@/components/Commander';
import Modal from '@/components/Modal';
import OrderDescriptions from '@/components/OrderDescriptions';
import PosButton from '@/components/PosButton';
import { OrderDetail, OrderEndpoint } from '@/models/order';
import { isPrintOrderLabelOpened, usePrintOrder, usePrintOrderLabel } from '@/utils/printer';
import { playSound } from '@/utils/sound';

export type OrderDetailModalProps = {
  orderSn?: string;
  orderId?: string;
  appointmentId?: string;
  onClose?: (hasChange: boolean, order: OrderDetail) => void;
  onRequestDelivery?: () => void;
  trigger?: React.ReactElement;
  zIndex?: number;
} & CommandEntityReady;

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  orderSn,
  orderId,
  appointmentId,
  onClose,
  onRequestDelivery = () => undefined,
  trigger,
  zIndex,
  onCommandEntityReady,
}: OrderDetailModalProps) => {
  const printLabel = usePrintOrderLabel();
  const printOrder = usePrintOrder();
  const [open, setOpen] = useState(false);
  const [hasDataChange, setHasDataChange] = useState(false);
  const { payload, setPayload, send, error, loaded, loading, reset } = useRequest<OrderDetail>(
    { url: `${OrderEndpoint}/query` },
    { manual: true }
  );

  const fetchData = () => {
    send({
      params: { orderSn: orderSn, orderId, appointmentId },
    }).then(data => {
      let title = '',
        subTitle;
      if (data.order) {
        title = `订单-${data.order.orderSn}`;
        subTitle = data.skuList?.[0]?.goodsName;
      }
      if (!title) {
        title = data.appointment ? `预约单` : '';
        subTitle = `预约时间: ${data.appointment.startTime}`;
      }
      onCommandEntityReady?.({
        type: CommandEntityTypeEnum.ORDER,
        key: data.order ? `o-${data.order.id}` : `a-${data.appointment.id}`,
        title: title,
        subTitle,
      });
    });
  };

  useEffect(() => {
    const hasData = !!orderSn || !!orderId || !!appointmentId;
    if (!trigger) {
      setOpen(hasData);
      if (hasData) {
        fetchData();
      } else {
        reset();
      }
    }
  }, [orderSn, orderId, appointmentId, trigger]);

  const onComplete = () => {
    setOpen(false);
    onClose?.(hasDataChange, payload);
  };

  return (
    <>
      {open ? (
        <Modal
          title={
            <>
              <FileDoneOutlined /> 订单与预约详情
            </>
          }
          zIndex={zIndex}
          open={open}
          destroyOnHidden
          onOk={onComplete}
          onCancel={onComplete}
          width={800}
          footer={[
            window.__POS_Device__ && payload && payload.skuList && isPrintOrderLabelOpened() ? (
              <PosButton
                key="print-tspl"
                onClick={() => {
                  playSound('click');
                  printLabel(payload.skuList, payload.order);
                }}>
                补打标签
              </PosButton>
            ) : null,
            window.__POS_Device__ && payload ? (
              <PosButton
                key="print-esc"
                onClick={() => {
                  playSound('click');
                  printOrder(payload.order, payload.skuList);
                }}>
                补打小票
              </PosButton>
            ) : null,
            <PosButton type="primary" key="back" onClick={onComplete}>
              关闭
            </PosButton>,
          ]}>
          <AsyncWrapper render={loaded} loading={loading} error={error}>
            {() => {
              if (!payload) return null;
              return (
                <div style={{ position: 'relative' }}>
                  <OrderDescriptions
                    // order={order}
                    // skuList={skuList}
                    // user={user}
                    // appointment={appointment}
                    postInvalidate={fetchData}
                    orderDetail={payload}
                    onDataChange={() => {
                      setHasDataChange(true);
                    }}
                  />
                </div>
              );
            }}
          </AsyncWrapper>
        </Modal>
      ) : null}
      {trigger
        ? React.cloneElement(trigger, {
            onClick: () => {
              setOpen(true);
              fetchData();
              trigger.props.onClick?.();
            },
          })
        : null}
    </>
  );
};
